export const SPECIAL_FINISH_MAT_TEMPLATE_ID = 122629

/** Template section IDs from Finish Mat template 122629 (stable across audits). */
export const FINISH_MAT_PRESS_SELECTION_SECTION_ID = '0.4488372727052816'
export const FINISH_MAT_GENERAL_INFO_SECTION_ID = '0.41435397966649856'
export const FINISH_MAT_FINISHED_MAT_SECTION_ID = '0.8719548127624807'

export const FINISH_MAT_SECTION_LAYOUT = {
    PORTAL: 'portal',
    HORIZONTAL: 'horizontal',
    DEFAULT: 'default',
}

/** @deprecated Use FINISH_MAT_GENERAL_INFO_SECTION_ID; kept for name fallback only. */
export const FINISH_MAT_GENERAL_INFO_SECTION_NAME = 'general info'

const FINISH_MAT_TABLE_SECTION_IDS = new Set([
    FINISH_MAT_FINISHED_MAT_SECTION_ID,
])

const FINISH_MAT_TABLE_SECTION_KEYS = ['finished mat', 'finish mat']

export const sectionIdsMatch = (a, b) => {
    const left = String(a ?? '').trim()
    const right = String(b ?? '').trim()
    return left !== '' && right !== '' && left === right
}

export const normalizeFinishMatSectionName = name =>
    String(name || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()

const matchesFinishMatGeneralInfoById = section =>
    sectionIdsMatch(section?.SectionId, FINISH_MAT_GENERAL_INFO_SECTION_ID)

const matchesFinishMatGeneralInfoByName = section => {
    const name = normalizeFinishMatSectionName(section?.SectionName)
    return name === FINISH_MAT_GENERAL_INFO_SECTION_NAME
}

const matchesFinishMatTableById = section =>
    FINISH_MAT_TABLE_SECTION_IDS.has(String(section?.SectionId ?? '').trim())

const matchesFinishMatTableByName = section => {
    const name = normalizeFinishMatSectionName(section?.SectionName)
    return FINISH_MAT_TABLE_SECTION_KEYS.some(key => name.includes(key))
}

export const isFinishMatGeneralInfoSection = section => {
    if (!section) {
        return false
    }
    return (
        matchesFinishMatGeneralInfoById(section) ||
        matchesFinishMatGeneralInfoByName(section)
    )
}

export const isFinishMatTableSection = section => {
    if (!section) {
        return false
    }
    return (
        matchesFinishMatTableById(section) ||
        matchesFinishMatTableByName(section)
    )
}

export const findFinishMatGeneralInfoSection = sections => {
    if (!Array.isArray(sections)) {
        return null
    }
    return (
        sections.find(
            section =>
                isFinishMatGeneralInfoSection(section) &&
                !section?.IsCopiedSection,
        ) || null
    )
}

export const getFinishMatSectionSortOrder = section =>
    Number(section?.Order ?? section?.SectionOrder ?? 0)

export const sortFinishMatSectionsByOrder = sections => {
    if (!Array.isArray(sections)) {
        return []
    }
    return [...sections].sort(
        (a, b) =>
            getFinishMatSectionSortOrder(a) - getFinishMatSectionSortOrder(b),
    )
}

export const filterFinishMatTableSections = sections => {
    if (!Array.isArray(sections)) {
        return []
    }
    return sortFinishMatSectionsByOrder(sections.filter(isFinishMatTableSection))
}

export const filterFinishMatGeneralInfoFromSections = sections => {
    if (!Array.isArray(sections)) {
        return []
    }
    return sections.filter(section => !isFinishMatGeneralInfoSection(section))
}

/** Sections that are not General Info or Finished Mat — render with default layout. */
export const filterFinishMatOtherSections = sections => {
    if (!Array.isArray(sections)) {
        return []
    }
    return sortFinishMatSectionsByOrder(
        sections.filter(
            section =>
                !isFinishMatGeneralInfoSection(section) &&
                !isFinishMatTableSection(section),
        ),
    )
}

export const getFinishMatSectionLayout = section => {
    if (isFinishMatGeneralInfoSection(section)) {
        return FINISH_MAT_SECTION_LAYOUT.PORTAL
    }
    if (isFinishMatTableSection(section)) {
        return FINISH_MAT_SECTION_LAYOUT.HORIZONTAL
    }
    return FINISH_MAT_SECTION_LAYOUT.DEFAULT
}

/** Ordered render groups: each section keeps template Order; layout follows section type. */
export const buildFinishMatOrderedRenderGroups = sections => {
    if (!Array.isArray(sections) || sections.length === 0) {
        return []
    }

    const groups = []
    sortFinishMatSectionsByOrder(sections).forEach(section => {
        const layout = getFinishMatSectionLayout(section)
        const lastGroup = groups[groups.length - 1]
        if (lastGroup && lastGroup.layout === layout) {
            lastGroup.sections.push(section)
            return
        }
        groups.push({ layout, sections: [section] })
    })
    return groups
}

export const isPortalCardSectionName = (sectionName, portalCardSectionNames) => {
    if (!portalCardSectionNames?.length) {
        return false
    }
    const name = normalizeFinishMatSectionName(sectionName)
    return portalCardSectionNames.some(
        key => normalizeFinishMatSectionName(key) === name,
    )
}

export const isPortalCardSection = (
    section,
    portalCardSectionIds = [],
    portalCardSectionNames = [],
) => {
    if (!section) {
        return false
    }
    const ids = Array.isArray(portalCardSectionIds) ? portalCardSectionIds : []
    if (
        ids.length > 0 &&
        ids.some(id => sectionIdsMatch(section?.SectionId, id))
    ) {
        return true
    }
    return isPortalCardSectionName(
        section?.SectionName,
        portalCardSectionNames,
    )
}
